import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useMe } from "@/components/portal/MeContext";
import { markSeen } from "@/components/portal/WelcomeChecklist";
import { PageHeader, Empty } from "@/components/portal/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLS = [["todo", "To do"], ["in_progress", "In progress"], ["done", "Done"]];
const EMPTY = { title: "", description: "", project_id: "", assignee_id: "", due_date: "" };

const NewTask = ({ open, onOpenChange, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState(EMPTY);
  const [people, setPeople] = useState([]);
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF(EMPTY);
    api("get", "/employees").then(setPeople);
    api("get", "/projects").then(setProjects);
  }, [open, api]);
  const save = async (e) => {
    e.preventDefault();
    try {
      onSaved(await api("post", "/tasks", f));
      onOpenChange(false);
      toast.success("Task assigned");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Assign a task</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3" data-testid="task-form">
          <Input required placeholder="Task title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title-input" />
          <Textarea placeholder="Details" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="task-desc-input" />
          <Select value={f.assignee_id || "none"} onValueChange={(v) => setF({ ...f, assignee_id: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Assign to" /></SelectTrigger>
            <SelectContent><SelectItem value="none">Unassigned</SelectItem>{people.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.name} ({p.role})</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.project_id || "none"} onValueChange={(v) => setF({ ...f, project_id: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="task-project-select"><SelectValue placeholder="Project" /></SelectTrigger>
            <SelectContent><SelectItem value="none">No project</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due-input" />
          <button className="w-full rounded-full bg-[#F2B91C] py-3 text-sm font-semibold text-[#0E1F3F] hover:bg-[#D9A30F]" data-testid="task-save-btn">Assign task</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Tasks() {
  const api = useApi();
  const me = useMe();
  const [tasks, setTasks] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api("get", "/tasks").then(setTasks);
    if (me.role === "employee") markSeen(api, "tasks");
  }, [api, me.role]);

  const move = async (t, status) => {
    try {
      const nt = await api("patch", `/tasks/${t.id}`, { status });
      setTasks(tasks.map((x) => (x.id === t.id ? nt : x)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (t) => {
    await api("delete", `/tasks/${t.id}`);
    setTasks(tasks.filter((x) => x.id !== t.id));
  };

  return (
    <div data-testid="tasks-page">
      <PageHeader eyebrow="Crew assignments" title={me.role === "admin" ? "Tasks" : "My tasks"}>
        {me.role === "admin" && <button onClick={() => setOpen(true)} data-testid="new-task-btn" className="inline-flex items-center gap-2 rounded-full bg-[#F2B91C] px-5 py-2.5 text-sm font-semibold text-[#0E1F3F] hover:bg-[#D9A30F]"><Plus size={16} />New task</button>}
      </PageHeader>
      {tasks && !tasks.length && <Empty text="No tasks yet." />}
      {tasks?.length > 0 && (
        <div className="grid gap-5 md:grid-cols-3">
          {COLS.map(([key, label]) => (
            <div key={key} className="rounded-2xl bg-[#F2EFE9] p-4" data-testid={`task-column-${key}`}>
              <p className="eyebrow mb-3">{label} · {tasks.filter((t) => t.status === key).length}</p>
              <div className="space-y-3">
                {tasks.filter((t) => t.status === key).map((t) => (
                  <div key={t.id} className="rounded-xl border border-[#E6E2DA] bg-white p-4" data-testid={`task-card-${t.id}`}>
                    <div className="flex justify-between gap-2"><p className="font-semibold">{t.title}</p>{me.role === "admin" && <button onClick={() => remove(t)} data-testid={`task-delete-${t.id}`}><Trash2 size={14} className="text-[#8C877D]" /></button>}</div>
                    {t.description && <p className="mt-1 text-sm text-[#5C5850]">{t.description}</p>}
                    <p className="mt-2 text-xs text-[#8C877D]">{t.assignee_name || "Unassigned"}{t.project_name ? ` · ${t.project_name}` : ""}{t.due_date ? ` · Due ${fmtDate(t.due_date)}` : ""}</p>
                    <div className="mt-3 flex gap-1.5">
                      {COLS.filter(([k]) => k !== key).map(([k, l]) => (
                        <button key={k} onClick={() => move(t, k)} data-testid={`task-move-${t.id}-${k}`} className="rounded-full border border-[#E6E2DA] px-2.5 py-1 text-[11px] font-semibold hover:border-[#F2B91C] hover:text-[#A67C00]">→ {l}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <NewTask open={open} onOpenChange={setOpen} onSaved={(t) => setTasks([t, ...(tasks || [])])} />
    </div>
  );
}
